import React from "react";
import DOMPurify from "dompurify";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../../styles/circuit-background.css";
import {
  faShieldAlt,
  faLock,
  faBug,
  faLaptopCode,
  faNetworkWired,
  faKey,
  faFish,
  faUsers,
  faServer,
  faDatabase,
  faHome,
  faChild,
  faMobileAlt,
  faGamepad,
  faCommentDots,
  faUserShield
} from "@fortawesome/free-solid-svg-icons";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import {
  fetchAwarenessThreatDetail,
  fetchAwareness,
} from "../../api/awareness";

const AwarenessDetail = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const { group, threatType } = useParams();

  // Mapping of group-specific icons for the banner
  const groupBannerIcons = {
    Individual: [faShieldAlt, faLock, faBug, faLaptopCode, faNetworkWired, faKey],
    Family: [faHome, faChild, faMobileAlt, faShieldAlt, faLock, faKey],
    Youth: [faGamepad, faMobileAlt, faCommentDots, faShieldAlt, faLock, faKey],
    Professional: [faShieldAlt, faLock, faBug, faLaptopCode, faNetworkWired, faKey],
  };

  // Mapping of threat icons
  const threatIcons = {
    Phishing: faFish,
    Malware: faBug,
    Ransomware: faLock,
    "Social Engineering": faUsers,
    "MitM Attacks": faNetworkWired,
    "Password Attacks": faKey,
    DDoS: faServer,
    "Data Breaches": faDatabase,
    "Tricky Messages": faFish,
    "Online Strangers": faUsers,
    "Gaming Scams": faGamepad,
    "Sketchy Apps": faMobileAlt,
    "Social Media Tricks": faCommentDots,
    "Bad Links": faLaptopCode,
    "Password Stealers": faKey,
    "App Bugs": faBug,
    "Family Phishing Scams": faFish,
    "Social Media Risks": faUsers,
    "Smart Device Hacks": faHome,
    "Kids' App Dangers": faChild,
    "Fake Shopping Sites": faLaptopCode,
    "Wi-Fi Eavesdropping": faNetworkWired,
    "Rogue Downloads": faBug,
    "Identity Theft": faUserShield,
    "Business Email Compromise": faFish,
    "Malware in Workflows": faBug,
  };

  // Fetch threat detail using useQuery
  const {
    data: threatData,
    isLoading: threatLoading,
    error: threatError,
  } = useQuery({
    queryKey: ["threatDetail", { group, lang, threatType }],
    queryFn: () => fetchAwarenessThreatDetail({ group, threatType, lang }),
    select: (data) => (Array.isArray(data) && data.length > 0 ? data[0] : null),
  });

  // Fetch threats for sidebar using useQuery
  const { data: threatsData, isLoading: threatsLoading } = useQuery({
    queryKey: ["threats", { group, lang }],
    queryFn: () => fetchAwareness({ lang, group }),
    select: (data) => data || [],
  });

  // Convert group to lowercase for consistent translation key usage
  const groupKey = group.toLowerCase();

  if (threatLoading || threatsLoading) {
    return (
      <>
        <PageHeader />
        <GradientBanner
          title={t(`awareness.${groupKey}.title`, `${group} Cybersecurity Training`)}
          description={t(
            `awareness.${groupKey}.subtitle`,
            `Default subtitle for ${group}`
          )}
          path={[
            { label: t("header.home", "Home"), link: "/" },
            { label: t(`header.${groupKey}`, group), link: `/${groupKey}` },
          ]}
          backgroundIcons={groupBannerIcons[group] || groupBannerIcons.Individual}
        />
        <FullscreenSpinner className="py-12" />
      </>
    );
  }

  if (threatError || !threatData) {
    return (
      <div className="text-center py-16 text-red-500">
        {threatError?.message || "Threat not found"}
      </div>
    );
  }

  const threat = threatData;
  const threats = threatsData;
  const threatTypeName = threat.threat_type?.[0] || "Unknown Threat";
  const translationSections = Array.isArray(threat.translations)
    ? threat.translations
    : [];

  return (
    <>
      <PageHeader />
      <GradientBanner
        title={t(`awareness.${groupKey}.title`, `${group} Cybersecurity Training`)}
        description={t(
          `awareness.${groupKey}.subtitle`,
          `Default subtitle for ${group}`
        )}
        path={[
          { label: t("header.home", "Home"), link: "/" },
          { label: t(`header.${groupKey}`, group), link: `/${groupKey}-training` },
        ]}
        backgroundIcons={groupBannerIcons[group] || groupBannerIcons.Individual}
      />

      <div className="circuit-page-background">
        <div className="max-xl:px-4 py-8 md:py-16 px-dynamic flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex-1 space-y-4"
          >
            {/* Modern Hero Section */}
            <div className="bg-gradient-to-br from-white via-blue-50/30 to-primary/5 rounded-3xl shadow-2xl overflow-hidden border border-gray-100/50">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 p-4 sm:p-6 md:p-8">
                <div className="lg:col-span-3 flex flex-col justify-center order-1 lg:order-1">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <FontAwesomeIcon
                        icon={threatIcons[threatTypeName] || faBug}
                        className="text-primary text-xl sm:text-2xl"
                      />
                    </div>
                    <div className="flex-1">
                      <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-heading font-catamaran leading-tight break-words">
                        {threatTypeName}
                      </h1>
                      <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-primary to-blue-600 rounded-full mt-2"></div>
                    </div>
                  </div>
                  <p className="text-gray-600 text-base sm:text-lg leading-relaxed font-nunito-sans">
                    {t('awareness.hero.description', 'Understanding and protecting against {{threat}} threats', { threat: threatTypeName.toLowerCase() })}
                  </p>
                </div>
                
                <div className="lg:col-span-2 flex items-center justify-center order-2 lg:order-2">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 }}
                    className="relative group w-full max-w-sm lg:max-w-none"
                  >
                    <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-primary/20 to-blue-600/20 rounded-2xl sm:rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-xl overflow-hidden border border-gray-100 w-full">
                      <img
                        src={threat.image || "/path-to-default-image.jpg"}
                        alt={`${threatTypeName} illustration`}
                        className="w-full h-48 sm:h-56 md:h-64 object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid gap-6">
                {translationSections.length === 0 ? (
                  <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
                    <FontAwesomeIcon icon={faShieldAlt} className="text-gray-400 text-3xl mb-4" />
                    <p className="text-gray-500 text-lg">{t('awareness.no_content', 'No content available')}</p>
                  </div>
                ) : (
                  translationSections.map((section, index) => (
                    <motion.article
                      key={`${section.id || section.title}-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(index * 0.1, 0.5) }}
                      className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
                    >
                      <div className="p-6 md:p-8">
                        <div className="flex items-start gap-4 mb-6">
                          <div className="w-1 h-12 bg-gradient-to-b from-primary to-blue-600 rounded-full flex-shrink-0 mt-1"></div>
                          <div>
                            <h2 className="text-2xl md:text-3xl font-bold text-heading mb-2 font-catamaran">
                              {section.title || t('awareness.section', 'Section')}
                            </h2>
                            <div className="w-16 h-0.5 bg-primary/30 rounded-full"></div>
                          </div>
                        </div>
                        
                        <div
                          className="prose prose-lg max-w-none text-gray-700 leading-relaxed font-nunito-sans
                                   prose-headings:text-heading prose-headings:font-catamaran
                                   prose-p:text-paragraph prose-p:leading-relaxed
                                   prose-strong:text-primary prose-strong:font-semibold
                                   prose-ul:space-y-2 prose-li:text-paragraph
                                   prose-a:text-primary prose-a:no-underline hover:prose-a:underline"
                          dangerouslySetInnerHTML={{ 
                            __html: DOMPurify.sanitize(section.content || '', { 
                              USE_PROFILES: { html: true }, 
                              FORBID_ATTR: ["style"], 
                              FORBID_TAGS: ["font"] 
                            }) 
                          }}
                        />
                      </div>
                    </motion.article>
                  ))
                )}
            </div>
          </motion.div>

          {/* Sidebar */}
          <div className="lg:w-80 xl:w-96">
            <motion.aside
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden sticky top-24 max-xl:top-32"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-blue-600 p-6 md:p-8 text-white relative overflow-hidden">
                <div className="absolute top-0 right-0 opacity-20">
                  <FontAwesomeIcon
                    icon={groupBannerIcons[group]?.[0] || faShieldAlt}
                    className="text-6xl transform rotate-12"
                  />
                </div>
                <div className="relative z-10">
                  <h2 className="text-2xl md:text-3xl font-bold mb-2 font-catamaran">
                    {t(
                      `awareness.${groupKey}.awareness.title`,
                      `${group} Cyber Awareness`
                    )}
                  </h2>
                  <p className="text-blue-100 font-nunito-sans">
                    {t(
                      `awareness.${groupKey}.awareness.subtitle`,
                      `Key threats for ${group.toLowerCase()}`
                    )}
                  </p>
                  <div className="w-16 h-1 bg-white/30 rounded-full mt-3"></div>
                </div>
              </div>

              {/* Threats List */}
              <div className="p-6 md:p-8">
                <h3 className="text-lg font-semibold text-heading mb-6 font-catamaran flex items-center gap-2">
                  <FontAwesomeIcon icon={faShieldAlt} className="text-primary" />
                  Related Threats
                </h3>
                
                <div className="space-y-3">
                  {threats.length > 0 ? (
                    threats.map((item, index) => {
                      const isActive = item.threat_type[0] === threatTypeName;
                      return (
                        <motion.div
                          key={`${item.group}-${item.threat_type[0]}`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          whileHover={{ x: 4 }}
                          className={`group flex items-center p-4 rounded-xl transition-all duration-300 cursor-pointer border-2 ${
                            isActive 
                              ? 'bg-primary/5 border-primary shadow-md' 
                              : 'bg-gray-50 border-transparent hover:bg-primary/5 hover:border-primary/20 hover:shadow-md'
                          }`}
                          onClick={() =>
                            navigate(
                              `/awareness-detail/${item.group}/${item.threat_type[0]}`
                            )
                          }
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 transition-colors ${
                            isActive 
                              ? 'bg-primary text-white' 
                              : 'bg-white text-primary group-hover:bg-primary group-hover:text-white'
                          }`}>
                            <FontAwesomeIcon
                              icon={threatIcons[item.threat_type[0]] || faBug}
                              className="text-sm"
                            />
                          </div>
                          <div className="flex-1">
                            <span className={`font-medium font-nunito-sans transition-colors ${
                              isActive ? 'text-primary' : 'text-paragraph group-hover:text-primary'
                            }`}>
                              {item.threat_type[0]}
                            </span>
                          </div>
                          {isActive && (
                            <div className="w-2 h-2 bg-primary rounded-full"></div>
                          )}
                        </motion.div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-gray-400 text-2xl mb-3" />
                      <p className="text-gray-500">No threats available for this group.</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default AwarenessDetail;